import type { FC, ReactNode } from 'react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { openIntercomLauncher } from '@gonfalon/intercom-fin-app/openIntercomLauncher';
import { useUpdateIntercomFlow } from '@gonfalon/intercom-fin-app/useUpdateIntercomFlow';
import { toFlags } from '@gonfalon/navigator/navigators';
import { Heading } from '@launchpad-ui/components';
import cx from 'clsx';

import { useOnboardingStorage, useSdkActivePolling, TROUBLESHOOTING_THRESHOLD_MS } from '../../hooks';
import { SdkActiveAlert } from '../SdkActive/SdkActiveAlert';
import { SdkSetupConnectionIndicator } from '../SdkSetupConnectionIndicator/SdkSetupConnectionIndicator';

import styles from './SdkSetupVerifyConnectionSection.module.css';

export interface SdkSetupVerifyConnectionSectionProps {
  /** Section heading; defaults to “Verify the connection”. */
  heading?: string;
  /** Merged with the root section layout (e.g. extra top margin in onboarding lists). */
  className?: string;
}

export const SdkSetupVerifyConnectionSection: FC<SdkSetupVerifyConnectionSectionProps> = ({
  heading = 'Verify the connection',
  className,
}) => {
  const headingId = useId();
  const { sdk, selectedProject } = useOnboardingStorage();
  // Match the manual tab: poll forever, keep polling through transient errors, and
  // track elapsed time so we can escalate the helper copy after a couple minutes.
  const { isActive, error, elapsedMs, refetch } = useSdkActivePolling(sdk, {
    keepPollingOnError: true,
    reportElapsed: true,
  });
  const { flowName } = useUpdateIntercomFlow();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleContactSupport = () => {
    openIntercomLauncher(flowName);
  };

  const handleTryAgain = () => {
    setIsRetrying(true);
    void refetch().finally(() => setIsRetrying(false));
  };

  // A failed poll surfaces the error state immediately. Polling continues in the
  // background (keepPollingOnError); a later success flips us back automatically.
  // While an explicit retry is in-flight, transition back to the indicator so
  // the user sees visual feedback that something is happening.
  if (error && !isActive && !isRetrying) {
    return (
      <section className={cx(styles.section, className)} aria-labelledby={headingId}>
        <Heading level={3} id={headingId} className={styles.heading}>
          {heading}
        </Heading>
        <SdkActiveAlert
          status="error"
          title="Error checking your connection"
          onTryAgain={handleTryAgain}
          onContactSupport={handleContactSupport}
        />
      </section>
    );
  }

  // Connected: link to the flags page so the user has a next action.
  const connectedText: ReactNode = selectedProject?.key ? (
    <>
      Connected! You're ready to build with LaunchDarkly,{' '}
      <Link to={toFlags({ projectKey: selectedProject.key })}>create your first flag</Link>.
    </>
  ) : (
    "Connected! You're ready to build with LaunchDarkly."
  );

  // Still polling after 2 min: surface a troubleshooting hint. Pulsing dot stays
  // so it's clear we're still checking; copy switches from "Watching" to "Still watching".
  const isStillPolling = elapsedMs >= TROUBLESHOOTING_THRESHOLD_MS;
  const inactiveText = isStillPolling ? 'Still watching for your first event…' : 'Watching for your first event…';
  const helperText = isStillPolling
    ? "Haven't detected your app yet. Check your agent finished installing and your app is running."
    : 'Install the SDK and run your app. Can take up to a minute to detect.';

  return (
    <section className={cx(styles.section, className)} aria-labelledby={headingId}>
      <Heading level={3} id={headingId} className={styles.heading}>
        {heading}
      </Heading>
      <SdkSetupConnectionIndicator
        isActive={isActive}
        activeText={connectedText}
        inactiveText={inactiveText}
        helperText={helperText}
      />
    </section>
  );
};
